import Navbar from "@/components/Navbar";
import { Building2, Users, Target, Award, TrendingUp, Shield, CheckCircle2, Store } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "react-router-dom";

export default function AboutUs() {
  const stats = [
    { icon: Users, label: "Active Users", value: "10,000+" },
    { icon: Building2, label: "Businesses Listed", value: "5,000+" },
    { icon: Award, label: "Verified Consultants", value: "500+" },
    { icon: TrendingUp, label: "Successful Deals", value: "2,000+" }
  ];

  const values = [
    {
      icon: Shield,
      title: "Trust & Transparency",
      description: "We verify every business listing and consultant to ensure authenticity and build trust in the marketplace."
    },
    {
      icon: Target,
      title: "Customer Success",
      description: "Your success is our priority. We provide comprehensive support throughout your business journey."
    },
    {
      icon: Users,
      title: "Community First",
      description: "Building a strong community of entrepreneurs, business owners, and consultants across India."
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Hero Section */}
        <div className="text-center mb-16" data-testid="about-header">
          <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6">
            About ReBusi
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            Redefine Business, Reclaim India. India's most trusted platform connecting business buyers, sellers, and expert consultants for seamless transactions and growth.
          </p>
        </div>

        {/* Stats */}
        <div className="grid md:grid-cols-4 gap-8 mb-20">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card key={index} className="text-center bg-white border-2 border-slate-100 hover:border-blue-200 transition-all">
                <CardContent className="pt-8 pb-6">
                  <Icon className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                  <p className="text-4xl font-bold text-slate-900 mb-2">{stat.value}</p>
                  <p className="text-sm text-slate-600">{stat.label}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Mission & Vision */}
        <div className="mb-20">
          <h2 className="text-4xl font-bold text-slate-900 mb-12 text-center">Our Mission & Vision</h2>
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Mission */}
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl transform group-hover:scale-105 transition-transform duration-300"></div>
              <div className="relative bg-white rounded-2xl p-10 shadow-lg border-2 border-blue-100 h-full">
                <div className="flex items-center gap-4 mb-6">
                  <div className="bg-blue-600 rounded-full p-4">
                    <Target className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-3xl font-bold text-slate-900">Our Mission</h3>
                </div>
                <div className="space-y-4 text-slate-700 leading-relaxed">
                  <p className="text-lg font-medium text-blue-900">
                    Empowering Entrepreneurs Across India
                  </p>
                  <p>
                    To democratize business transactions in India by providing a <strong>secure, transparent, and efficient platform</strong> where entrepreneurs can buy, sell, and grow their businesses with confidence.
                  </p>
                  <ul className="space-y-3 mt-6">
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span>Make business buying/selling accessible to everyone</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span>Connect entrepreneurs with expert consultants</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span>Build trust through verified listings and transparency</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span>Support business growth at every stage</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Vision */}
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-100 to-pink-100 rounded-2xl transform group-hover:scale-105 transition-transform duration-300"></div>
              <div className="relative bg-white rounded-2xl p-10 shadow-lg border-2 border-purple-100 h-full">
                <div className="flex items-center gap-4 mb-6">
                  <div className="bg-purple-600 rounded-full p-4">
                    <TrendingUp className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-3xl font-bold text-slate-900">Our Vision</h3>
                </div>
                <div className="space-y-4 text-slate-700 leading-relaxed">
                  <p className="text-lg font-medium text-purple-900">
                    Building India's #1 Business Ecosystem
                  </p>
                  <p>
                    To become the <strong>most trusted and comprehensive ecosystem</strong> for business transactions and consulting services across India, empowering millions of entrepreneurs to achieve their business goals.
                  </p>
                  <div className="mt-6 space-y-4">
                    <div className="bg-purple-50 rounded-lg p-4 border border-purple-100">
                      <p className="font-semibold text-purple-900 mb-1">By 2030</p>
                      <p className="text-sm text-slate-700">Facilitate ₹10,000+ Crore in business transactions annually</p>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4 border border-purple-100">
                      <p className="font-semibold text-purple-900 mb-1">Pan-India Reach</p>
                      <p className="text-sm text-slate-700">Serve 1 Million+ entrepreneurs across all major cities</p>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4 border border-purple-100">
                      <p className="font-semibold text-purple-900 mb-1">Trusted Network</p>
                      <p className="text-sm text-slate-700">Build a community of 10,000+ verified consultants</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Story */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-12 mb-20">
          <h2 className="text-4xl font-bold text-slate-900 mb-6 text-center">Our Story</h2>
          <div className="max-w-4xl mx-auto space-y-4 text-lg text-slate-700 leading-relaxed">
            <p>
              ReBusi was founded in 2024 with a simple yet powerful vision: to Redefine how business transactions happen in India and Reclaim the entrepreneurial spirit of our nation. We recognized that buying or selling a business was often a complex, opaque process filled with challenges.
            </p>
            <p>
              Our founders, experienced entrepreneurs themselves, understood the pain points firsthand. They built ReBusi to bridge the gap between buyers, sellers, and expert consultants, creating a transparent ecosystem backed by technology and trust.
            </p>
            <p>
              Today, ReBusi serves thousands of users across India, facilitating business transactions worth crores and providing expert consultation services that help businesses scale and succeed. We're on a mission to Reclaim India's business potential.
            </p>
          </div>
        </div>

        {/* Values */}
        <div className="mb-20">
          <h2 className="text-4xl font-bold text-slate-900 mb-12 text-center">Our Core Values</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <Card key={index} className="bg-white border border-slate-100 hover:shadow-lg transition-all">
                  <CardContent className="p-8">
                    <Icon className="w-12 h-12 text-blue-600 mb-4" />
                    <h3 className="text-xl font-bold text-slate-900 mb-3">{value.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Team Section */}
        <div className="text-center bg-white rounded-2xl p-12 shadow-sm border border-slate-100">
          <h2 className="text-4xl font-bold text-slate-900 mb-6">Join Our Journey</h2>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto mb-8">
            We're building the future of business transactions in India. Whether you're looking to buy, sell, or grow your business, ReBusi is here to support you every step of the way. Together, let's Reclaim India!
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/marketplace" className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors" data-testid="explore-marketplace-btn">
              Explore Marketplace
            </Link>
            <Link to="/franchises" className="bg-purple-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors" data-testid="explore-franchises-btn">
              Explore Franchises
            </Link>
            <Link to="/contact" className="bg-slate-100 text-slate-900 px-8 py-3 rounded-lg font-semibold hover:bg-slate-200 transition-colors" data-testid="contact-us-btn">
              Contact Us
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}